import { CSSProperties, ChangeEventHandler, forwardRef, useMemo, useState } from 'react';

import clsx from 'clsx';
import styled from 'styled-components';

import { useFormErrorContext } from '../Form/FormError';

import { InnerInput, InputWrapper } from './Input.styled';

import type { BaseInputProps } from './types';

export interface InputPassword extends BaseInputProps {
  value?: string;
  innerInputStyle?: CSSProperties;
  /**
   * onChange event handler
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

export const InputPassword = forwardRef<HTMLInputElement, InputPassword>((props, ref) => {
  const {
    value,
    block = true,
    status,
    style,
    innerInputStyle,
    onChange,
    disabled,
    ...args
  } = props;
  const { error } = useFormErrorContext();
  const [visiblePassword, setVisiblePassword] = useState<boolean>(false);

  const isError = useMemo(() => {
    if (status === false) return false;
    if (error) return true;
    return status === 'error';
  }, [status, error]);

  return (
    <InputWrapper className={clsx({ disabled, block, error: isError })} style={style}>
      <InnerInput
        ref={ref}
        type={visiblePassword ? 'text' : 'password'}
        value={value || ''}
        style={innerInputStyle}
        onChange={onChange}
        {...args}
      />
      <ToggleButton type="button" onClick={() => setVisiblePassword((prev) => !prev)}>
        {visiblePassword ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 10C12.5 10.663 12.2366 11.2989 11.7678 11.7678C11.2989 12.2366 10.663 12.5 10 12.5C9.33696 12.5 8.70107 12.2366 8.23223 11.7678C7.76339 11.2989 7.5 10.663 7.5 10C7.5 9.33696 7.76339 8.70107 8.23223 8.23223C8.70107 7.76339 9.33696 7.5 10 7.5C10.663 7.5 11.2989 7.76339 11.7678 8.23223C12.2366 8.70107 12.5 9.33696 12.5 10Z"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.25911 10.5916C2.10736 10.2119 2.10736 9.7881 2.25911 9.40845C3.48684 6.33684 6.4905 4.16669 10 4.16669C13.5103 4.16669 16.5133 6.33683 17.7409 9.40845C17.8926 9.78811 17.8926 10.2119 17.7409 10.5916C16.5133 13.6632 13.5103 15.8334 10 15.8334C6.4905 15.8334 3.48684 13.6632 2.25911 10.5916Z"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.5 7.5C15.5 9.7225 13 10.8333 10 10.8333C7 10.8333 4.5 9.7225 2.5 7.5"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.5 12.5L4.58333 9.33334"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M17.5 12.48L15.4233 9.33334"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.5 14.1667L7.91667 10.8333"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12.5002 14.1667L12.0835 10.8333"
              stroke="#404040"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </ToggleButton>
    </InputWrapper>
  );
});

const ToggleButton = styled.button`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  color: #404040;

  background: none;
  outline: none;
  border: none;
`;
